import { BackgroundCheckAnalytics } from '@/backend/features/background-check/shared';
import { PageHeaderWithImage } from '@juvare/common-web-ui/PageHeader';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { TransparentBox } from '@juvare/common-web-ui/TransparentBox';
import { getLicenseCheckLatestStatusAnalyticsFunctionDef } from '@juvare/credentialing/backend/features/credentialing-verification/shared';
import { LicenseCheckAnalyticsWidget } from '@juvare/credentialing/ui/features/credentialing-verification/components';
import JasBoardsStatusTable from '@juvare/credentialing/ui/features/jas-boards/components/JasBoardsStatusTable';
import { useTypedPlatformFunctionResponse } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import { useFunctionClient } from '@juvare/platform-sdk-react/ui/hooks';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import screeningsIllustration from '../../../assets/images/screeningsIllustration.svg';
import { BackgroundCheckSummary } from '../../background-check';
import { useScreeningsTab } from '../hooks';
import { ScreeningsTab } from '../hooks/useScreeningsTab';

interface ScreeningsPageProps {
    showBackgroundCheckSummary: boolean;
}

export const ScreeningsPage: React.FC<ScreeningsPageProps> = ({ showBackgroundCheckSummary }) => {
    const { t } = useTranslation();
    const functionClient = useFunctionClient();
    const [backgroundChecksNeedAction, setBackgroundChecksNeedAction] = React.useState(0);
    const [backgroundCheckAnalytics, setBackgroundCheckAnalytics] = React.useState<BackgroundCheckAnalytics[]>([]);
    const { screeningsTab, setScreeningsTab } = useScreeningsTab();

    React.useEffect(() => {
        functionClient
            .executeFunction({
                functionName: 'getBackgroundCheckAnalytics',
            })
            .then(({ result }) => {
                setBackgroundCheckAnalytics(result);
                const checksNeedAction = result.reduce((total: number, current: BackgroundCheckAnalytics) => {
                    total += current.failed + current.ready;
                    return total;
                }, 0);
                setBackgroundChecksNeedAction(checksNeedAction);
            })
            .catch(handleErrorSnackbar);
    }, [functionClient]);

    const { data: credentialVerificationAnalytics } = useTypedPlatformFunctionResponse(getLicenseCheckLatestStatusAnalyticsFunctionDef, {});

    const handleTabChange = (_e: React.SyntheticEvent, tab: ScreeningsTab) => {
        setScreeningsTab(tab);
    };

    return (
        <Box>
            <PageHeaderWithImage
                gradient='cyan-light'
                title={t('pages.screenings.pageTitle')}
                subtitle={t('pages.screenings.pageSubtitle')}
                image={screeningsIllustration}
                imageAlt=''
            >
                <Box
                    sx={{
                        columnGap: { xs: 2, lg: 3 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        rowGap: { xs: 2, sm: 0 },
                    }}
                    data-test='screenings-dashboard-summary'
                >
                    <SummaryBox
                        count={credentialVerificationAnalytics?.manual.verifiedExpiring ?? 0}
                        text={t('pages.screenings.credentialsExpiringSoon')}
                        dataTest='credential-verification-transparent-box'
                    />
                    <SummaryBox
                        count={backgroundChecksNeedAction}
                        text={t('pages.screenings.backgroundChecksNeedAction')}
                        textTooltip={t('pages.screenings.backgroundChecksNeedActionInfo')}
                        dataTest='background-checks-transparent-box'
                        dataTestTooltip='background-checks-summary-tooltip'
                    />
                </Box>
            </PageHeaderWithImage>
            <Box
                sx={{
                    pl: 4,
                    borderBottom: 1,
                    borderColor: 'divider',
                    boxShadow: 1,
                    zIndex: 1,
                }}
            >
                <Tabs variant='scrollable' onChange={handleTabChange} value={screeningsTab}>
                    <Tab value='screenings' label={t('pages.screenings.tabs.screenings')} />
                    <Tab value='integrations' label={t('pages.screenings.tabs.integration')} />
                </Tabs>
            </Box>
            {screeningsTab === 'screenings' && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        p: 4,
                    }}
                >
                    <LicenseCheckAnalyticsWidget summaryPageRoute='./credentials' />
                    {showBackgroundCheckSummary && <BackgroundCheckSummary analyticsData={backgroundCheckAnalytics} />}
                </Box>
            )}
            {screeningsTab === 'integrations' && <JasBoardsStatusTable />}
        </Box>
    );
};

const SummaryBox: React.FC<{
    count: number;
    text: string;
    textTooltip?: string;
    dataTest?: string;
    dataTestTooltip?: string;
}> = (props) => {
    return (
        <Box sx={{ flexBasis: '50%' }}>
            <TransparentBox data-test={props.dataTest ?? ''}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    <Typography variant='h4' component='p' sx={{ wordWrap: 'break-word' }}>
                        {props.count}
                    </Typography>
                    <Stack direction='row'>
                        <Typography variant='body2' sx={{ wordWrap: 'break-word' }}>
                            {props.text}
                        </Typography>
                        {props.textTooltip && (
                            <Tooltip enterTouchDelay={0} title={props.textTooltip} data-test={props.dataTestTooltip}>
                                <InfoOutlinedIcon sx={{ ml: 1 }} fontSize='small' />
                            </Tooltip>
                        )}
                    </Stack>
                </Box>
            </TransparentBox>
        </Box>
    );
};
